'use client';

import { ICartItem } from '@/api/types/cart';
import Image from '@/components/Image';
import { IS_DE_FR } from '@/utils/constants';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { CloseOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';

interface LimitBuyDialogProps {
  open: boolean;
  onClose: () => void;
  cartItemData: ICartItem;
  itemPromotion?: any;
}

export default function LimitBuyDialog({
  open,
  onClose,
  cartItemData,
  itemPromotion,
}: LimitBuyDialogProps) {
  const t = useTranslations('cart.limitBuyDialog');
  const globalT = useTranslations('cart');
  const product = cartItemData?.product || ({} as any);

  // 限购价格提示
  const getCutPrice = () => {
    const { product: { limit_buy, limit_rule } = {} } = cartItemData;
    // ○ 当游客加购，或登录用户在促销规则周期内未购买过该商品时，说明文案为：
    //   ■ 每人每商品限购*件：X cut-price items per account only
    //   ■ 每单每商品限购*件、每单多商品合计限购*件：X cut-price items per order only
    // ○ 当登录用户在促销规则周期内购买过该商品时，说明文案为：
    //   ■ 每人每商品限购*件：X cut-price items per account only
    // limit_rule 单品促销限购规则 1不限制 2每人每商品限购 3每单每商品限制 4每单多商品合计限购
    if (!limit_buy) {
      return '';
    }
    let str = '';
    if (limit_rule === 2 || limit_rule === 5) {
      str = 'account';
    } else if (limit_rule === 3 || limit_rule === 4) {
      str = 'order';
    } else {
      return '';
    }
    return `${limit_buy} ${limit_buy > 1 ? globalT('limitBuyDialog.cutPriceItemsPlural') : globalT('limitBuyDialog.cutPriceItems')} ${str === 'account' ? globalT('limitBuyDialog.perAccount') : globalT('limitBuyDialog.perOrder')}`;
  };

  // 优惠仅适用于镜框说明
  const getOnlyForFrames = () => {
    const { product: { is_glass = {} } = {} } = cartItemData || {};
    // 是否镜框 1是 2否
    if (is_glass === 2) {
      return t('discountOnlyForFrames');
    } else {
      return '';
    }
  };

  // 不可退换
  const getNoReturn = () => {
    // products_promotions_type 未参与促销 1常规促销 2flash sale 3Seckill
    // products_promotions_show 是否显示促销文案1显示 2不显示
    const { products_promotions_type, products_promotions_show, over_limit_rule } =
      itemPromotion || {};
    if (
      Number(products_promotions_type) === 3 &&
      products_promotions_show === 1 &&
      over_limit_rule === 1
    ) {
      return t('nonReturnableOrExchange');
    } else {
      return '';
    }
  };

  // 第二件半价互斥
  const getHalfPrice = () => {
    const { product: { exclusion: { half_price } = {} } = {} } = cartItemData || {};
    // 第二件半价互斥规则 1是 2否
    if (half_price === 1) {
      return t('halfPriceInvalid');
    } else {
      return '';
    }
  };

  const cutPrice = getCutPrice();
  const onlyForFrames = getOnlyForFrames();
  const noReturn = getNoReturn();
  const halfPrice = getHalfPrice();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '16px',
          padding: '24px 25px 20px',
          width: '85vw',
          maxWidth: '400px',
        },
      }}
    >
      <span className="text-[16px] font-bs text-center">{t('title')}</span>

      <DialogContent sx={{ padding: '20px 0 0', textAlign: 'center' }}>
        {/* 商品信息 */}
        <div className="flex flex-col items-center mb-[20px]">
          <div className="flex items-center justify-center w-[88px] h-[88px] border border-[#dce0e8] rounded-[5px] mb-2">
            <Image
              src={product.img}
              alt={product.short_name}
              width={71}
              height={71}
              className="block w-[71px] h-auto object-contain"
            />
          </div>
          <p className="text-[14px] text-center mt-[5px]">{product.short_name}</p>
        </div>

        {/* 限购规则列表 */}
        <ul className="" style={IS_DE_FR ? { textAlign: 'start' } : {}}>
          {cutPrice && (
            <li className="flex items-center justify-start">
              <span className="text-[13px] mr-[5px]">•</span>
              <span className="text-[13px]">{cutPrice}</span>
            </li>
          )}

          {halfPrice && (
            <li className="flex items-center justify-start">
              <span className="text-[13px] mr-[5px]">•</span>
              <span className="text-[13px]">{halfPrice}</span>
            </li>
          )}

          {noReturn && (
            <li className="flex items-center justify-start">
              <span className="text-[13px] mr-[5px]">•</span>
              <span className="text-[13px]">{noReturn}</span>
            </li>
          )}

          {onlyForFrames && (
            <li className="flex items-center justify-start">
              <span className="text-[13px] mr-[5px]">•</span>
              <span className="text-[13px]">{onlyForFrames}</span>
            </li>
          )}
        </ul>
      </DialogContent>

      {/* 关闭按钮 */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: '#282828',
          fontSize: '18px',
          fontWeight: 'bold',
        }}
      >
        <CloseOutlined />
      </IconButton>
    </Dialog>
  );
}
